import { Button, TextField } from "@stagvista/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import * as React from "react";

import { AuthLayout } from "../components/auth-layout.js";
import { useCloudSession } from "../hooks/use-cloud-session.js";
import { CloudApiError } from "../lib/cloud-api-client.js";

export const Route = createFileRoute("/forgot-password")({
  component: ForgotPasswordPage,
});

function ForgotPasswordPage(): React.ReactElement {
  const { client } = useCloudSession();
  const [email, setEmail] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [sent, setSent] = React.useState(false);

  async function handleSubmit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      // BR-AUTH-9: `POST /v1/auth/forgot` always responds the same way
      // whether or not `email` is registered — this page's own success
      // state must stay just as generic, or it would leak account
      // existence right back out through the UI.
      await client.request<Record<string, never>>("POST", "/v1/auth/forgot", { email });
      setSent(true);
    } catch (err) {
      setError(
        err instanceof CloudApiError
          ? (err.problem.detail ?? err.problem.title)
          : "Couldn't send that request.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout title="Reset your password" subtitle="We'll email you a link to set a new one.">
      {sent ? (
        <p className="text-body text-fg-secondary">
          If an account exists for that email, a password-reset link is on its way. Check your
          inbox.
        </p>
      ) : (
        <form className="flex flex-col gap-4" onSubmit={(e) => void handleSubmit(e)}>
          <TextField
            label="Email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={error ?? undefined}
          />
          <Button type="submit" loading={submitting} className="mt-2">
            Send reset link
          </Button>
        </form>
      )}
      <Link to="/sign-in" className="mt-4 block text-center text-caption text-brand-primary">
        Back to sign in
      </Link>
    </AuthLayout>
  );
}
